<template>
  <v-card class="text-center mb-4 blue-grey lighten-5">
    <v-overlay
      :value="value.status === 'notReady'"
      class="notReady-overlay"
      color="secondary"
      opacity="0.7"
      absolute
    >
      <v-sheet
        class="rounded-lg pa-3 transition-swing accent"
        v-text="$t('animation.multiproject.status.notReady.message')"
      />
    </v-overlay>

    <v-card-text class="py-1 px-1">
      <v-row align="center" no-gutters>
        <v-col
          :md="ccpm ? 6 : 12"
          :lg="ccpm ? 3 : 4"
          cols="12"
          class="py-2"
        >
          <project-information :value="value" :ccpm="ccpm" />
        </v-col>
        <v-col
          v-if="ccpm"
          cols="12"
          md="6"
          lg="auto"
        >
          <ag-fever-chart
            :key="`fever-chart-${value.id}`"
            :value="value"
            :size="{ height: 120, width: 200 }"
            class="mx-auto"
          />
        </v-col>
        <v-col
          :lg="ccpm ? '' : 8"
          cols="12"
          md="12"
          class="py-2"
        >
          <gantt-chart
            :value="value.gantt"
            :overlay-message="ganttOverlayMessage"
            :highlight-key="ccpm ? 'enableNextProjectToStart' : ''"
          />
        </v-col>
      </v-row>
    </v-card-text>

    <v-divider />

    <v-card flat color="blue-grey lighten-5" class="py-1">
      <div class="text-center">
        <v-icon
          :color="$t(`animation.multiproject.status.${value.status}.color`)"
          class="px-2"
          dense
          v-text="$t(`animation.multiproject.status.${value.status}.icon`)"
        />
        <div
          class="d-inline-block"
          style="height: 20px; line-height: 20px;"
          v-text="$t(`animation.multiproject.status.${value.status}.message`)"
        />
      </div>

      <div class="player-auto-switch" style="z-index: 10;">
        <ag-confirm-dialog
          :title="$t('general.confirmation')"
          :message="toggleModeAutoConfirmMessage"
          :cancel-label="$t('general.cancel')"
          :confirm-label="$t('general.validate')"
          validation-btn-color="accent"
          @confirm="toggleMode"
        >
          <template v-slot:activator="{ on }">
            <v-switch
              :input-value="value.auto"
              :disabled="!isRealPlayer || loadingModeAuto"
              :loading="loadingModeAuto"
              :readonly="isRealPlayer && !loadingModeAuto"
              :label="$t('animation.multiproject.general.auto')"
              class="mt-0 pt-0"
              color="accent"
              hide-details
              v-on="on"
            />
          </template>
        </ag-confirm-dialog>
      </div>
    </v-card>
  </v-card>
</template>

<script>
import AgFeverChart from 'src/components/common/AgFeverChart'
import AgConfirmDialog from 'src/components/common/AgConfirmDialog'
import ProjectInformation from './ProjectInformation'
import GanttChart from './GanttChart'

export default {
  name: 'PlayerCard',
  components: {
    AgFeverChart,
    AgConfirmDialog,
    ProjectInformation,
    GanttChart,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    ccpm: {
      type: Boolean,
      default: false,
    },
  },
  inject: [
    'sendPlayerAction',
  ],
  data () {
    return {
      loadingModeAuto: false,
    }
  },
  computed: {
    playerId () {
      return this.value.id
    },
    ganttOverlayMessage () {
      if (this.value.projectDuration) {
        const duration = this.value.projectDuration
        const durationText = this.$t('animation.multiproject.general.projectDone', { duration })

        if (this.ccpm) {
          return durationText
        }

        const changes = this.value.historyWeekEndPlanned.length
        const changesText = this.$tc('animation.multiproject.general.numberChanges', changes, { changes })
        return `${durationText}, ${changesText}`
      }
      return ''
    },
    isRealPlayer () {
      return !!this.value.trainingSessionPlayerId
    },
    toggleModeAutoConfirmMessage () {
      const messageKey = this.value.auto
        ? 'disableModeAuto'
        : 'enableModeAuto'
      return this.$t(`animation.multiproject.general.confirm.${messageKey}`, {
        name: this.value.playerName
          || this.$t('animation.multiproject.general.notPlayerName').toLowerCase(),
      })
    },
  },
  methods: {
    async toggleMode () {
      this.loadingModeAuto = true
      const actionName = this.value.auto
        ? 'disableModeAuto'
        : 'enableModeAuto'
      await this.sendPlayerAction(this.playerId, actionName)
      this.loadingModeAuto = false
    },
  },
}
</script>

<style lang="scss" scoped>
  .notReady-overlay {
    transition: 1s cubic-bezier(0.25, 0.8, 0.5, 1), z-index 1ms;
  }

  .player-auto-switch {
    position: absolute;
    top: 2px;
    right: 10px;
  }
</style>
